import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Alert } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { saveDraft, loadDraft, clearDraft, hasDraft, isDraftExpired } from '../utils/draftUtils';

const DraftSaveButton = ({ 
  formKey, 
  uhidNo, 
  formData, 
  onDraftLoaded, 
  // Backward-compat props seen on some pages
  draftKey, // maps to formKey
  uhid_no,  // maps to uhidNo
  disabled = false,
  variant = "outlined",
  size = "medium",
  // Optionally allow caller to force-hide in update mode
  hidden = false,
  isUpdate = false
}) => {
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [draftExists, setDraftExists] = useState(false);
  const [draftData, setDraftData] = useState(null);

  // Normalize legacy props → current props
  const effectiveFormKey = formKey || draftKey;
  const effectiveUhidNo = uhidNo || uhid_no;

  // Check for existing draft when component mounts
  React.useEffect(() => {
    if (effectiveUhidNo && effectiveFormKey) {
      const existingDraft = loadDraft(effectiveFormKey, effectiveUhidNo);
      if (existingDraft) {
        setDraftExists(true);
        setDraftData(existingDraft);
      }
    }
  }, [effectiveUhidNo, effectiveFormKey]);

  const handleSaveDraft = () => {
    if (!effectiveFormKey || !effectiveUhidNo) {
      alert('Unable to save draft: Missing form key or UHID');
      return;
    }

    const success = saveDraft(effectiveFormKey, effectiveUhidNo, formData);
    if (success) {
      setDraftExists(true);
      alert('Draft saved successfully! You can continue filling this form later.');
    } else {
      alert('Failed to save draft. Please try again.');
    }
  };

  const handleLoadDraft = () => {
    if (draftData && onDraftLoaded) {
      onDraftLoaded(draftData.formData);
      setShowDraftDialog(false);
      alert('Draft loaded successfully!');
    }
  };

  const handleOverwriteDraft = () => {
    if (!effectiveFormKey || !effectiveUhidNo) {
      alert('Unable to save draft: Missing form key or UHID');
      return;
    }
    const success = saveDraft(effectiveFormKey, effectiveUhidNo, formData);
    if (success) {
      const updated = loadDraft(effectiveFormKey, effectiveUhidNo);
      setDraftExists(true);
      setDraftData(updated);
      setShowDraftDialog(false);
      alert('Draft updated successfully!');
    } else {
      alert('Failed to update draft. Please try again.');
    }
  };

  const handleDiscardDraft = () => {
    if (clearDraft(effectiveFormKey, effectiveUhidNo)) {
      setDraftExists(false);
      setDraftData(null);
      setShowDraftDialog(false);
      alert('Draft discarded successfully!');
    } else {
      alert('Failed to discard draft. Please try again.');
    }
  };

  const handleShowDraftDialog = () => {
    setShowDraftDialog(true);
  };

  const isExpired = draftData ? isDraftExpired(draftData) : false;
  const draftAge = draftData ? Math.round(getDraftAge(draftData) * 10) / 10 : 0;

  // Hide the button if: forced hidden, in update mode, or missing required keys
  const shouldHideButton = hidden || isUpdate || !effectiveFormKey;

  return (
    <>
      {!shouldHideButton && (
      <Button
        variant={variant}
        startIcon={<SaveIcon />}
        onClick={draftExists ? handleShowDraftDialog : handleSaveDraft}
        disabled={disabled || !effectiveUhidNo}
        size={size}
        color="primary"
        sx={{ mr: 1 }}
      >
        {draftExists ? 'Load Draft' : 'Save Draft'}
      </Button>
      )}

      <Dialog open={showDraftDialog} onClose={() => setShowDraftDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Draft Found</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            A draft was found for this form. What would you like to do?
          </Typography>
          
          {isExpired && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This draft is {draftAge} hours old and may be outdated.
            </Alert>
          )}
          
          <Typography variant="body2" color="text.secondary">
            Draft saved: {draftData?.timestamp ? new Date(draftData.timestamp).toLocaleString() : 'Unknown'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDraftDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleDiscardDraft} color="error">
            Discard Draft
          </Button>
          {/* <Button onClick={handleLoadDraft}>
            Load Draft
          </Button>
          <Button onClick={handleOverwriteDraft} variant="contained">
            Overwrite Draft
          </Button> */}
        </DialogActions>
      </Dialog>
    </>
  );
};

// Helper function to get draft age
const getDraftAge = (draft) => {
  if (!draft || !draft.timestamp) return 0;
  const draftTime = new Date(draft.timestamp);
  const now = new Date();
  return (now - draftTime) / (1000 * 60 * 60); // hours
};

export default DraftSaveButton;
